import { useState } from 'react'
import { useAdminTenants, useAdminTenant } from '@/hooks/useAdminTenants'
import {
  useTenantFeatureOverrides,
  useSyncTenantFeatures,
  type TenantFeatureOverride,
} from '@/hooks/useTenantFeatures'
import TenantFeatureTable from './TenantFeatureTable'

export default function TenantFeaturesTab() {
  const [selectedTenantId, setSelectedTenantId] = useState<string>('')
  const [showOverridesModal, setShowOverridesModal] = useState(false)

  const { data: tenantsResponse, isLoading: tenantsLoading } = useAdminTenants({ limit: 1000 })
  const { data: selectedTenant } = useAdminTenant(selectedTenantId || null)
  const { data: overrides } = useTenantFeatureOverrides(selectedTenantId || null)
  const syncMutation = useSyncTenantFeatures()

  const tenants = tenantsResponse?.tenants || []

  const handleSync = async () => {
    if (!selectedTenantId) return

    if (!confirm('Sync features based on current plan? This will recalculate feature access.')) {
      return
    }

    try {
      await syncMutation.mutateAsync(selectedTenantId)
      alert('Features synced successfully!')
    } catch (error) {
      alert('Failed to sync features: ' + (error as Error).message)
    }
  }

  return (
    <div className="space-y-6">
      {/* Tenant Selector */}
      <div className="bg-[#141c24]/80 border border-[#2a3542] rounded-sm p-5">
        <label className="block text-xs text-[#8b949e] font-mono mb-3 uppercase tracking-wider">
          Select Tenant
        </label>
        {tenantsLoading ? (
          <div className="text-xs text-[#8b949e] font-mono">Loading tenants...</div>
        ) : (
          <select
            value={selectedTenantId}
            onChange={(e) => setSelectedTenantId(e.target.value)}
            className="w-full bg-[#0a0e14] border border-[#2a3542] rounded px-3 py-2
                       text-[#e8eaed] font-mono text-sm
                       focus:outline-none focus:border-[#FFB84D]/50
                       focus:shadow-[0_0_15px_rgba(255,184,77,0.1)]"
          >
            <option value="">-- Select a tenant --</option>
            {tenants.map((tenant) => (
              <option key={tenant.id} value={tenant.id}>
                {tenant.name} ({tenant.slug}) - {tenant.plan} - {tenant.status}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Tenant Info Card & Actions */}
      {selectedTenant && (
        <div className="bg-[#141c24]/80 border border-[#2a3542] rounded-sm p-5">
          <div className="flex items-start justify-between gap-4 mb-4">
            {/* Tenant Info */}
            <div className="flex-1">
              <h3 className="text-lg font-mono text-[#FFB84D] mb-3">
                {selectedTenant.name}
              </h3>
              <div className="grid grid-cols-2 gap-3 text-sm font-mono">
                <div>
                  <span className="text-[#8b949e]">Slug:</span>{' '}
                  <span className="text-[#e8eaed]">{selectedTenant.slug}</span>
                </div>
                <div>
                  <span className="text-[#8b949e]">Plan:</span>{' '}
                  <span className="text-[#e8eaed] uppercase font-semibold">{selectedTenant.plan}</span>
                </div>
                <div>
                  <span className="text-[#8b949e]">Status:</span>{' '}
                  <StatusBadge status={selectedTenant.status} />
                </div>
                <div>
                  <span className="text-[#8b949e]">Users:</span>{' '}
                  <span className="text-[#e8eaed]">{selectedTenant.user_count}</span>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col gap-2">
              <button
                onClick={handleSync}
                disabled={syncMutation.isPending}
                className="px-4 py-2 bg-[#2a3542] border border-[#FFB84D]/30 rounded
                           text-[#FFB84D] font-mono text-xs uppercase tracking-wider
                           hover:bg-[#FFB84D]/10 hover:shadow-[0_0_15px_rgba(255,184,77,0.15)]
                           disabled:opacity-50 disabled:cursor-not-allowed
                           transition-all duration-200 whitespace-nowrap"
              >
                {syncMutation.isPending ? 'Syncing...' : '🔄 Sync Plan Features'}
              </button>
              <button
                onClick={() => setShowOverridesModal(true)}
                className="px-4 py-2 bg-[#2a3542] border border-[#2a3542] rounded
                           text-[#8b949e] font-mono text-xs uppercase tracking-wider
                           hover:text-[#FFB84D] hover:border-[#FFB84D]/30
                           transition-all duration-200 whitespace-nowrap"
              >
                📋 View Overrides
                {overrides && overrides.length > 0 && (
                  <span className="ml-2 px-1.5 py-0.5 bg-[#FFB84D] text-[#0a0e14] rounded text-xs font-bold">
                    {overrides.filter(o => o.is_active).length}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Feature Table */}
      {selectedTenantId && selectedTenant ? (
        <div className="bg-[#141c24]/40 border border-[#2a3542] rounded-sm p-5">
          <h3 className="text-sm font-mono uppercase tracking-wider text-[#8b949e] mb-4">
            Feature Access Control
          </h3>
          <TenantFeatureTable tenantId={selectedTenantId} tenantName={selectedTenant.name} />
        </div>
      ) : (
        <div className="bg-[#141c24]/80 border border-[#2a3542] rounded-sm p-12 text-center">
          <div className="text-[#8b949e] font-mono text-sm">
            Select a tenant to manage feature access
          </div>
        </div>
      )}

      {/* Overrides Modal */}
      {showOverridesModal && selectedTenantId && (
        <OverridesModal
          tenantId={selectedTenantId}
          tenantName={selectedTenant?.name || ''}
          overrides={overrides || []}
          onClose={() => setShowOverridesModal(false)}
        />
      )}
    </div>
  )
}

// =============================================================================
// HELPER COMPONENTS
// =============================================================================

function StatusBadge({ status }: { status: string }) {
  const colorClasses = {
    active: 'bg-green-500/10 text-green-400 border-green-500/30',
    pending: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30',
    suspended: 'bg-red-500/10 text-red-400 border-red-500/30',
    rejected: 'bg-gray-500/10 text-gray-400 border-gray-500/30',
  }

  const colorClass = colorClasses[status as keyof typeof colorClasses] || colorClasses.pending

  return (
    <span className={`px-2 py-0.5 rounded text-xs font-mono border ${colorClass} uppercase`}>
      {status}
    </span>
  )
}

// =============================================================================
// OVERRIDES MODAL
// =============================================================================

interface OverridesModalProps {
  tenantId: string
  tenantName: string
  overrides: TenantFeatureOverride[]
  onClose: () => void
}

function OverridesModal({ tenantName, overrides, onClose }: OverridesModalProps) {
  const activeOverrides = overrides.filter((o) => o.is_active)
  const expiredOverrides = overrides.filter((o) => !o.is_active)

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/70 z-50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto">
        <div
          className="bg-[#0a0e14] border border-[#FFB84D]/30 rounded-lg shadow-[0_0_50px_rgba(255,184,77,0.2)]
                     w-full max-w-3xl my-8 max-h-[90vh] overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="border-b border-[#FFB84D]/20 p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-2xl">📋</span>
                <div>
                  <h2 className="text-xl font-mono uppercase tracking-wider text-[#FFB84D]">
                    Feature Overrides
                  </h2>
                  <div className="text-xs text-[#8b949e] font-mono mt-1">
                    Tenant: {tenantName}
                  </div>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-[#8b949e] hover:text-[#FFB84D] transition-colors text-2xl"
              >
                ✕
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* Active Overrides */}
            <div>
              <h3 className="text-sm font-mono uppercase tracking-wider text-[#8b949e] mb-3">
                Active Overrides ({activeOverrides.length})
              </h3>
              {activeOverrides.length === 0 ? (
                <div className="bg-[#141c24]/60 border border-[#2a3542] rounded p-4 text-center">
                  <div className="text-xs text-[#8b949e] font-mono">
                    No active overrides
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  {activeOverrides.map((override) => (
                    <OverrideCard key={override.feature_key} override={override} />
                  ))}
                </div>
              )}
            </div>

            {/* Expired Overrides */}
            {expiredOverrides.length > 0 && (
              <div>
                <h3 className="text-sm font-mono uppercase tracking-wider text-[#8b949e] mb-3">
                  Expired Overrides ({expiredOverrides.length})
                </h3>
                <div className="space-y-2 opacity-60">
                  {expiredOverrides.map((override) => (
                    <OverrideCard key={override.feature_key} override={override} />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-[#2a3542] p-4 flex justify-end">
            <button
              onClick={onClose}
              className="px-5 py-2 bg-[#FFB84D] rounded
                         text-[#0a0e14] font-mono text-sm uppercase tracking-wider font-bold
                         hover:bg-[#FFB84D]/90
                         transition-all duration-200"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

function OverrideCard({ override }: { override: TenantFeatureOverride }) {
  return (
    <div className="bg-[#141c24]/80 border border-[#2a3542] rounded-sm p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="text-[#e8eaed] font-semibold mb-1">
            {override.feature_name}
          </div>
          <div className="text-xs text-[#8b949e] font-mono mb-2">
            [{override.feature_key}]
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs font-mono">
            <div>
              <span className="text-[#8b949e]">Granted by:</span>{' '}
              <span className="text-[#e8eaed]">{override.granted_by_name}</span>
            </div>
            <div>
              <span className="text-[#8b949e]">Granted:</span>{' '}
              <span className="text-[#e8eaed]">
                {new Date(override.granted_at).toLocaleDateString()}
              </span>
            </div>
            {override.expires_at && (
              <div className="col-span-2">
                <span className="text-[#8b949e]">Expires:</span>{' '}
                <span className={override.is_active ? 'text-amber-400' : 'text-red-400'}>
                  {new Date(override.expires_at).toLocaleString()}
                </span>
              </div>
            )}
          </div>
        </div>
        <div>
          {override.is_active ? (
            <span className="px-2 py-1 rounded text-xs font-mono bg-green-500/10 text-green-400 border border-green-500/30">
              ACTIVE
            </span>
          ) : (
            <span className="px-2 py-1 rounded text-xs font-mono bg-red-500/10 text-red-400 border border-red-500/30">
              EXPIRED
            </span>
          )}
        </div>
      </div>
    </div>
  )
}
